import { useUnifiedEvent } from '../contexts/UnifiedEventContext';
import { resolveEventAccess } from '../utils/permissions';
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

export type ResourceType = 'tasks' | 'guests' | 'budget' | 'timeline' | 'tasks_and_guests';
export type AccessLevel = 'view' | 'edit' | 'manage';
export type Permission = 'read' | 'create' | 'update' | 'delete' | 'share' | 'manage_collaborators';

interface PermissionCheck {
  resource: ResourceType;
  level?: AccessLevel;
  permission?: Permission;
}

interface PermissionResult {
  hasAccess: boolean;
  accessLevel: AccessLevel | null;
  isOwner: boolean;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canShare: boolean;
  canManageCollaborators: boolean;
  restrictionReason?: string;
}

// Client presentation policy. Firestore rules remain the authority for stored data.
export function usePermissions() {
  const { currentUser } = useAuth();
  const {
    currentUnifiedEvent,
    loading: eventLoading,
    teamMemberships: members,
  } = useUnifiedEvent();
  return useMemo(() => {
    const isOwner = Boolean(
      currentUser && !currentUser.isAnonymous && currentUnifiedEvent?.userId === currentUser.uid
    );
    const checkPermission = (check: PermissionCheck): PermissionResult => {
      const membership = members.find(m => m.teamKey === currentUnifiedEvent?.teamKey);
      const teamGrant = membership
        ? {
            weddingId: membership.event.id,
            resourceType: 'custom',
            resourceIds: membership.modules,
            accessLevel: membership.role,
          }
        : null;
      const accessLevel = resolveEventAccess(
        currentUser,
        currentUnifiedEvent,
        [teamGrant],
        check.resource
      );
      const permissions: Permission[] =
        accessLevel === 'manage'
          ? ['read', 'create', 'update', 'delete', 'share', 'manage_collaborators']
          : accessLevel === 'edit'
            ? ['read', 'create', 'update']
            : accessLevel === 'view'
              ? ['read']
              : [];
      const ranks = { view: 0, edit: 1, manage: 2 };
      const hasAccess =
        accessLevel !== null &&
        (!check.level || ranks[accessLevel] >= ranks[check.level]) &&
        (!check.permission || permissions.includes(check.permission));
      return {
        hasAccess,
        accessLevel,
        isOwner,
        canRead: permissions.includes('read'),
        canWrite: permissions.includes('update'),
        canDelete: permissions.includes('delete'),
        canShare: isOwner && permissions.includes('share'),
        canManageCollaborators: isOwner,
        ...(!hasAccess ? { restrictionReason: 'Accès non autorisé pour cet événement.' } : {}),
      };
    };
    return {
      isOwner,
      checkPermission,
      canAccess: (resource: ResourceType, level?: AccessLevel) =>
        checkPermission({ resource, ...(level ? { level } : {}) }).hasAccess,
      canRead: (resource: ResourceType) =>
        checkPermission({ resource, permission: 'read' }).hasAccess,
      canWrite: (resource: ResourceType) =>
        checkPermission({ resource, permission: 'update' }).hasAccess,
      canDelete: (resource: ResourceType) =>
        checkPermission({ resource, permission: 'delete' }).hasAccess,
      canShare: (resource: ResourceType) =>
        isOwner && checkPermission({ resource, permission: 'share' }).hasAccess,
      canManageCollaborators: () => isOwner,
      getAccessInfo: (resource: ResourceType) => checkPermission({ resource }),
      loading: eventLoading,
    };
  }, [currentUser, currentUnifiedEvent, eventLoading, members]);
}
export default usePermissions;
